// Generic async-export state machine — shared by every list export in PAM.
//
// The BE contract every export endpoint follows:
//
//   1. POST <startPath>              -> { export_id, status: "queued" }
//   2. GET  <statusPath(export_id)>  (poll)
//         -> queued | running | succeeded | failed | cancelled
//   3. On `succeeded`, the payload contains a presigned `download_url` that
//      matches the shared `PresignedDownloadData` shape — anchor-click it
//      via `triggerBrowserDownload`.
//
// Callers wire this into a thin domain hook (e.g. `useUsersExport`) that
// pins the two paths + the copy for `startError` / `failedDefault` /
// `timeout`. Everything else (POST + poll + terminal handling + client
// cap + auto-download) is shared.
//
// Guarantees the state machine holds regardless of caller:
//   - Double-start guard: while `isBusy` is true, `start()` is a no-op.
//   - Client cap: exports typically resolve in 3–15s (up to ~3 min for a
//     large dump); a configurable cap surfaces `failed` even if the BE
//     never returns a terminal status.
//   - Unmount cleanup: react-query stops polling when the mutation state
//     resets, and the cap timer is torn down explicitly.
//   - Poll 404 → treated as `failed`; a `succeeded` payload without a
//     `download_url` is *also* treated as `failed` (broken BE invariant,
//     polling has already stopped so nothing else can rescue us).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";

import { getPrivateInstance } from "./_privateInstance";
import { triggerBrowserDownload } from "../utils/triggerBrowserDownload";
import type {
  AsyncExportStartResponse,
  AsyncExportStatusData,
  AsyncExportStatusResponse,
  AsyncExportState,
  AsyncExportStrings,
} from "./useAsyncExport.types";

export type { AsyncExportState, AsyncExportStrings } from "./useAsyncExport.types";

export const ASYNC_EXPORT_START_KEY = "ASYNC_EXPORT_START";
export const ASYNC_EXPORT_STATUS_KEY = "ASYNC_EXPORT_STATUS";
/** Default ms between poll ticks. Aligned with the 3–15s BE completion band. */
export const ASYNC_EXPORT_POLL_INTERVAL_MS = 2500;
/** Default client cap — a hung export bubbles up as `failed` at this bound. */
export const ASYNC_EXPORT_CLIENT_CAP_MS = 5 * 60 * 1000;

const DEFAULT_STRINGS: Required<AsyncExportStrings> = {
  startError: "Couldn't start the export. Try again in a moment.",
  failedDefault: "Export failed. Start a new export to retry.",
  timeout: "Export is taking longer than expected. Start a new export to retry.",
};

type ExportPhase = "idle" | "preparing";

export type UseAsyncExportOptions = {
  /** POST endpoint that starts the export job. */
  readonly startPath: string;
  /** Builder for the poll endpoint given a returned export id. */
  readonly statusPath: (exportId: string) => string;
  /** Override the 2.5s default. */
  readonly pollIntervalMs?: number;
  /** Override the 5-minute default. */
  readonly clientCapMs?: number;
  /** Domain-specific error copy. Falls back to generic defaults. */
  readonly strings?: AsyncExportStrings;
  /** react-query keys — override to isolate cache namespaces per domain. */
  readonly queryKeys?: {
    readonly start?: string;
    readonly status?: string;
  };
};

const isTerminal = (status: string | undefined): boolean =>
  status === "succeeded" || status === "failed" || status === "cancelled";

/**
 * @internal Exported for tests. Reads the flat + nested envelope shapes the
 * axios wrappers may hand us and returns the export status body, or `null`
 * when the shape is unusable.
 */
export const extractStatusData = (
  response: unknown,
): AsyncExportStatusData | null => {
  const body = (response as { data?: unknown })?.data;
  const nested = (body as AsyncExportStatusResponse | undefined)?.data;
  const data = nested ?? body;
  if (!data || typeof data !== "object") return null;
  if (typeof (data as AsyncExportStatusData).status !== "string") return null;
  return data as AsyncExportStatusData;
};

export const extractStartData = (
  responseBody: unknown,
): AsyncExportStartResponse["data"] | null => {
  const nested = (responseBody as AsyncExportStartResponse | undefined)?.data;
  const data = nested ?? responseBody;
  if (!data || typeof data !== "object") return null;
  if (typeof (data as AsyncExportStartResponse["data"]).export_id !== "string") {
    return null;
  }
  return data as AsyncExportStartResponse["data"];
};

/**
 * POST + poll the async export state machine and auto-download on success.
 * Returns `{ isBusy, error, start, reset }` — everything a button needs to
 * render its disabled/spinner/error UI. See the file header for the full
 * contract; wrap this in a domain hook that pins the endpoint paths and
 * error copy for a specific export.
 */
export function useAsyncExport<Payload = Record<string, unknown>>(
  options: UseAsyncExportOptions,
): AsyncExportState<Payload> {
  const {
    startPath,
    statusPath,
    pollIntervalMs = ASYNC_EXPORT_POLL_INTERVAL_MS,
    clientCapMs = ASYNC_EXPORT_CLIENT_CAP_MS,
    strings,
    queryKeys,
  } = options;

  const copy: Required<AsyncExportStrings> = { ...DEFAULT_STRINGS, ...strings };
  const startKey = queryKeys?.start ?? ASYNC_EXPORT_START_KEY;
  const statusKey = queryKeys?.status ?? ASYNC_EXPORT_STATUS_KEY;

  const [phase, setPhase] = useState<ExportPhase>("idle");
  const [exportId, setExportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCapTimer = useCallback((): void => {
    if (capTimerRef.current) {
      clearTimeout(capTimerRef.current);
      capTimerRef.current = null;
    }
  }, []);

  const reset = useCallback((): void => {
    clearCapTimer();
    setPhase("idle");
    setExportId(null);
    setError(null);
  }, [clearCapTimer]);

  // POST — kicks the job. On success we stash the id so the poll query
  // enables. On network / 4xx we surface the error and reset to idle.
  const startMutation = useMutation({
    mutationKey: [startKey, startPath],
    mutationFn: async (payload: Payload) => {
      const response = await getPrivateInstance().post(startPath, payload);
      return (response as { data?: unknown })?.data;
    },
    onSuccess: (data) => {
      const id = extractStartData(data)?.export_id;
      if (!id) {
        // BE returned 202 but no id — treat as a start failure and let the
        // caller retry rather than silently stalling on a phantom poll.
        message.error(copy.startError);
        setError(copy.startError);
        setPhase("idle");
        return;
      }
      setExportId(id);
    },
    onError: () => {
      message.error(copy.startError);
      setError(copy.startError);
      setPhase("idle");
    },
  });

  // GET — polls the status endpoint until the job reaches a terminal state.
  // `refetchInterval` returns `false` on terminal statuses so react-query
  // stops the interval; the useEffect below then reads `data` and either
  // fires the download or surfaces the failure.
  const statusQuery = useQuery({
    queryKey: [statusKey, exportId],
    enabled: Boolean(exportId),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: async ({ signal }: any) => {
      const response = await getPrivateInstance().get(
        statusPath(exportId as string),
        { signal },
      );
      return extractStatusData(response);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetchInterval: (query: any) => {
      const status = query?.state?.data?.status as string | undefined;
      if (isTerminal(status)) return false;
      return pollIntervalMs;
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Terminal-state handler. Splitting this off keeps the mutation/query
  // definitions declarative and the side effects (download, toast, reset) in
  // a single spot with a clear dependency list.
  useEffect(() => {
    if (!exportId) return;

    if (statusQuery.isError) {
      // Poll 404 (or any transport failure). Treated as a failed export so
      // the button snaps back to idle rather than hanging on a spinner.
      message.error(copy.failedDefault);
      setError(copy.failedDefault);
      setPhase("idle");
      setExportId(null);
      clearCapTimer();
      return;
    }

    const data = statusQuery.data;
    if (!data) return;

    if (data.status === "succeeded") {
      // Defensive: `succeeded` without a `download_url` is a broken BE
      // invariant — polling has already stopped (terminal state), so if we
      // don't surface it here the button hangs on "preparing" until the
      // client cap fires with a misleading "timeout" message.
      if (data.download_url) {
        triggerBrowserDownload(data.download_url);
        setError(null);
      } else {
        message.error(copy.failedDefault);
        setError(copy.failedDefault);
      }
      setPhase("idle");
      setExportId(null);
      clearCapTimer();
      return;
    }

    if (data.status === "failed" || data.status === "cancelled") {
      const msg = data.error || copy.failedDefault;
      message.error(msg);
      setError(msg);
      setPhase("idle");
      setExportId(null);
      clearCapTimer();
    }
  }, [
    statusQuery.data,
    statusQuery.isError,
    exportId,
    clearCapTimer,
    copy.failedDefault,
  ]);

  // Client cap. Runs once per exportId — mounts the timer when we start
  // polling and tears it down on any transition to idle.
  useEffect(() => {
    if (!exportId) return undefined;
    capTimerRef.current = setTimeout(() => {
      message.error(copy.timeout);
      setError(copy.timeout);
      setPhase("idle");
      setExportId(null);
    }, clientCapMs);

    return () => {
      if (capTimerRef.current) {
        clearTimeout(capTimerRef.current);
        capTimerRef.current = null;
      }
    };
  }, [exportId, clientCapMs, copy.timeout]);

  const start = useCallback(
    (payload: Payload): void => {
      // Double-start guard: the caller also enforces this via `disabled`,
      // but parallel start() calls would race the mutation and confuse the
      // poll.
      if (phase === "preparing") return;
      setError(null);
      setPhase("preparing");
      startMutation.mutate(payload);
    },
    [phase, startMutation],
  );

  const isBusy = useMemo(() => phase === "preparing", [phase]);

  return { isBusy, error, start, reset };
}

export default useAsyncExport;
